import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../../../hooks/api";
import getQueryValue from "../../../../utils/getQueryValue";
import { Row, Col, Table, Dropdown, DropdownButton, Badge, Container as Grid, Alert, Nav } from "react-bootstrap";

import { connect } from "react-redux";
import Loading from "../../../../components/core/blocks/Loading";
import { getURLPrefix } from "../../../../utils/getUrlPrefix";
import { formatCurrency, formatNumber, formatPercent } from "../../../../utils/formatNumber";
import axios from "axios";
import { apiClient } from "../../../../zodios/client";
import { getBadgeVariant } from "../../../../modules/product_quality/utils";
import ContentEditor2 from "../../../../modules/product_quality/ContentEditor2";
import {
  HIGHLIGHT_RECOMMENDED_LENGTH,
  TITLE_MAX_LENGTH,
  TITLE_RECOMMENDED_LENGTH,
} from "../../../../modules/product_quality/constants";
import { Button } from "../../../../components/core/basic/Button";
import ConversionLegend from "../../../../modules/product_quality/components/ConversionLegend";
import SideMenuTop from "../../../../modules/product_quality/components/SideMenuTop";
import SideMenuBottom from "../../../../modules/product_quality/components/SideMenuBottom";
import ConfirmationModal from "../../../../components/core/blocks/ConfirmationModal";
import api from "../../../../utils/api";
import BulletPointEditor from "../../../../modules/product_quality/components/BulletPointEditor";
import { useMutation } from "@tanstack/react-query";
import { selectModules } from "../../../../redux/selectors/modules";
import useMinLoading from "../../../../hooks/useMinLoading";
import SteppedProgressBar from "../../../../components/custom/SteppedProgressBar";
import PublishHistory from "../../../../modules/product_quality/components/PublishHistory";
import useNav from "../../../../hooks/useNav";
import useCategories from "../../../../hooks/useCategories";
import { ProductBreadcrumbs } from "../../../../modules/perf_reports/product_report/ProductBreadcrumbPicker";
import { DatesProvider } from "../../../../dates/DatesContext";
import { HStack, VStack } from "../../../../../styled-system/jsx";
import { TextAreaField } from "../../../../components/fields/base/TextAreaField";
import { css } from "../../../../../styled-system/css";
import { Modal } from "../../../../components/core/basic/Modal";
import { useSimpleCan } from "../../../../config/casl/casl";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import { errorToast } from "../../../../utils/toasts";

const URL_PREFIX = getURLPrefix();

const ProductSEO = ({ marketPlace }) => {
  const { t } = useTranslation(["product_quality"]);
  const productId = getQueryValue("id");

  const [title, setTitle] = useState(null);
  const [featureBullets, setFeatureBullets] = useState([]);
  const [genericKeywords, setGenericKeywords] = useState("");
  const [description, setDescription] = useState("");
  const [highlight, setHighlight] = useState("");
  const [images, setImages] = useState([]);
  const [keyword, setKeyword] = useState(null);
  const [searchTerms, setSearchTerms] = useState(null);
  const [saveDate, setSaveDate] = useState(null);
  const [publishDate, setPublishDate] = useState(null);
  const [loadingSearchTerms, setLoadingSearchTerms] = useState(false);
  const [editorMode, setEditorMode] = useState("edit");
  const [showTitleHelp, setShowTitleHelp] = useState(false);
  const [showBulletHelp, setShowBulletHelp] = useState(false);
  const [showHighlightHelp, setShowHighlightHelp] = useState(false);
  const [variations, setVariations] = useState([]);
  const [contentChanged, setContentChanged] = useState(false);
  const [error, setError] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [currentNav, navProps] = useNav(["content", "publish_history"]);
  const [showRecomendModal, setShowRecomendModal] = useState(false);
  const [generationNotes, setGenerationNotes] = useState("");

  const userCanUpdateProducts = useSimpleCan("update", "Product");
  const fullDateTime = useDateFormatter("fullDateTime");

  const generateContent = useMutation({
    mutationFn: async (notes) => {
      const body = {
        product_id: productId,
        kw: keyword,
      };
      if (notes) {
        body.fb = notes;
      }
      return await api.get(`/merchandise/generate_content/`, body);
    },

    onMutate: () => {
      setRecommendations(null);
    },

    onSuccess: (data) => {
      setRecommendations(data.data?.completion);
    },
  });
  const { loading: loadingGeneratedContent, step } = useMinLoading(generateContent.isPending, 4, 3000);

  let scores = null;
  let product = null;
  let seoKeywords = null;
  let seoSearchTerms = null;
  let hiddenKeywords = null;

  const { loading, data } = useFetch(["productQuality", productId], `/merchandise/quality/${productId}`, {}, {});

  scores = data?.data;
  product = data?.data?.product;
  seoKeywords = data?.data?.seo_keywords?.keywords;
  seoSearchTerms = data?.data?.seo_keywords?.search_terms;
  hiddenKeywords = data?.data?.hidden_keywords;

  useEffect(() => {
    let isSaved = false;
    if (product) {
      if (scores.save_date !== null) {
        setSaveDate(fullDateTime.format(new Date(scores.save_date)));

        isSaved = true;
        setTitle(scores.revision.title);
        setDescription(scores.revision.description);
        setFeatureBullets(scores.revision.feature_bullets.map((b) => b.replace("&nbsp;", " ")));
        setGenericKeywords(scores.revision.generic_keywords);
        setHighlight(scores.revision.highlight ?? "");
      }
      if (scores.publish_date !== null) {
        setPublishDate(fullDateTime.format(new Date(scores.publish_date)));
      }
      getCatalog(isSaved);
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const allContentElements = useMemo(() => {
    const cleanDescription = description?.replaceAll(/<[^>]+>/g, "");
    const bullets =
      featureBullets?.reduce((acc, bullet, index) => {
        acc[`featureBullet${index + 1}`] = bullet;
        return acc;
      }, {}) ?? {};
    return {
      title,
      description: cleanDescription,
      genericKeywords,
      ...bullets,
    };
  }, [featureBullets, title, description, genericKeywords]);

  if (seoKeywords && seoSearchTerms) {
    [seoKeywords, seoSearchTerms].forEach((data) => {
      for (let i = 0; i < data.length; i++) {
        const kw = data[i]["keyword_text"];
        const kwData = data[i];
        kwData.cr = 0;
        kwData.ctr = 0;
        kwData.acos = 0;

        if (kw.length <= 1) {
          continue;
        }

        if (kwData.clicks > 0) {
          kwData.cr = kwData.conversions / kwData.clicks;
        }
        if (kwData.impressions > 0) {
          kwData.ctr = kwData.clicks / kwData.impressions;
        }
        if (kwData.sales > 0) {
          kwData.acos = kwData.cost / kwData.sales;
        }

        if (!kwData.cr) {
          kwData.cr = 0;
        }
      }
    });

    seoKeywords.sort(function (a, b) {
      return b.cr - a.cr;
    });

    seoSearchTerms.sort(function (a, b) {
      return b.cr - a.cr;
    });
  }

  const category = product?.product_category ?? "";

  const getSearchTerms = async (monogram, product_id) => {
    if (loadingSearchTerms) {
      return;
    }
    // setQuickSearch(null);
    setEditorMode("metrics");
    setKeyword(monogram);
    setLoadingSearchTerms(true);

    const response = await axios.get(
      `${URL_PREFIX}/merchandise/search_terms?monogram=${monogram["keyword_text"]}&product_id=${product_id}`,
    );

    const sts = [];
    for (let i = 0; i < response.data.search_terms.length; i++) {
      const st = response.data.search_terms[i];

      st.ctr = 0;
      st.cr = 0;
      st.acos = 0;

      if (st.impressions > 0) {
        st.ctr = st.clicks / st.impressions;
      }
      if (st.clicks > 0) {
        st.cr = st.conversions / st.clicks;
      }
      if (st.cost > 0 && st.sales > 0) {
        st.acos = st.cost / st.sales;
      }
      sts.push(st);
    }
    sts.sort(function (a, b) {
      return b.impressions - a.impressions;
    });

    setSearchTerms(sts);
    setLoadingSearchTerms(false);
  };

  const getCatalog = async (isSaved) => {
    let response;
    try {
      response = await apiClient.getCatalog({ queries: { product_id: product.id } });
    } catch (error) {
      errorToast({
        message: t(
          ($) => $.product_quality["Failed to load product catalog data. Please try again or contact support."],
        ),
        error,
      });
      return;
    }

    if (response?.catalog) {
      const catalog = response.catalog;

      const images = {
        thumbnail: {},
        main: {},
      };

      if (catalog?.images) {
        catalog.images[0]["images"].forEach((image) => {
          if (images["thumbnail"][image.variant] === undefined && image.width <= 100) {
            images["thumbnail"][image.variant] = image.link;
          }
          if (images["main"][image.variant] === undefined && image.width >= 1000) {
            images["main"][image.variant] = image.link;
          }
        });
      }
      setImages(images);

      let parentASIN = null;
      if (catalog?.relationships?.[0]?.relationships?.[0]?.parentAsins?.[0]) {
        parentASIN = catalog.relationships[0].relationships[0].parentAsins[0];
        getVariations(parentASIN);
      }

      if (!isSaved) {
        setTitle(catalog.attributes?.item_name?.value?.[0]?.value);
        if (catalog?.attributes?.bullet_point?.value) {
          const bullets = catalog.attributes.bullet_point.value;
          const bulletValues = [];
          [0, 1, 2, 3, 4].forEach((i) => {
            if (!bullets[i]) {
              bulletValues[i] = "";
            } else {
              bulletValues.push(bullets[i]["value"]);
            }
          });

          setFeatureBullets(bulletValues.map((b) => b.replace("&nbsp;", " ")));
        }

        if (catalog?.attributes?.generic_keyword?.value) {
          if (catalog.attributes.generic_keyword.value.length > 0) {
            setGenericKeywords(catalog.attributes.generic_keyword.value[0].value);
          }
        }
        if (catalog?.attributes?.product_description?.value) {
          if (catalog.attributes.product_description.value.length > 0) {
            setDescription(catalog.attributes.product_description.value[0].value);
          }
        }
        if (catalog?.attributes?.title_differentiation?.value?.length > 0) {
          setHighlight(catalog.attributes.title_differentiation.value[0].value);
        }
      }
    }
  };

  const getVariations = async (parentASIN) => {
    const response = await axios.get(
      `${URL_PREFIX}/merchandise/quality/variations?product_id=${product.id}&parent_asin=${parentASIN}`,
    );
    if (response.data) {
      setVariations(response.data);
    }
  };

  const onContentChange = () => {
    setContentChanged(true);
  };

  const saveProduct = async () => {
    const revision = {
      title: title,
      description: description,
      feature_bullets: featureBullets.filter((b) => b !== ""),
      generic_keywords: genericKeywords,
      highlight: highlight,
    };
    scores.revision = revision;
    scores.save_date = new Date();
    scores.publish_date = null;
    setContentChanged(false);

    const res = await api.put(`/merchandise/quality/${productId}/`, scores);

    if (res.status === 200) {
      scores.save_date = res.data.save_date;
      setSaveDate(fullDateTime.format(new Date(res.data.save_date)));
    }
  };

  const publishProduct = async () => {
    const revision = {
      title: title,
      description: description,
      feature_bullets: featureBullets.filter((b) => b !== ""),
      generic_keywords: genericKeywords,
      highlight: highlight,
    };
    scores.revision = revision;
    scores.publish = true;
    setContentChanged(false);

    let res;
    try {
      res = await api.put(`/merchandise/quality/${productId}/`, scores);
    } catch (err) {
      setError(err.response.data);
      return;
    }

    if (res.status === 200) {
      scores.save_date = null;
      scores.publish_date = res.data.publish_date;
      setPublishDate(fullDateTime.format(new Date(res.data.publish_date)));
      setSaveDate(null);
    }
  };

  const resetProduct = async () => {
    scores.revision = null;
    scores.reset = true;
    setContentChanged(false);
    setError(null);

    const res = await api.put(`/merchandise/quality/${productId}/`, scores);

    if (res.status === 200) {
      setSaveDate(null);
      getCatalog();
    }
  };

  const onRemoveBullet = (index) => {
    const bullets = [...featureBullets];
    bullets.splice(index, 1);
    setFeatureBullets(bullets);
  };

  const setContent = (field, content) => {
    if (field.indexOf("featureBullets") > -1) {
      const index = parseInt(field.split(".")[1]);
      // Add Feature Bullet
      if (index > featureBullets.length - 1) {
        const bullets = [...featureBullets];
        bullets.push(content);
        setFeatureBullets(bullets);
        return;
      }
      const bullets = [...featureBullets];
      bullets[index] = content;
      setFeatureBullets(bullets);
      return;
    }

    switch (field) {
      case "title":
        setTitle(content);
        break;
      case "description":
        setDescription(content);
        break;
      case "genericKeywords":
        setGenericKeywords(content);
        break;
      case "highlight":
        setHighlight(content);
        break;
      default:
        break;
    }
  };

  const { currentCategoryDetails: categoryDetails, categories: categoryNavData } = useCategories(
    category,
    "content/quality",
  );

  if (loading || !product) {
    return <Loading text={t(($) => $.product_quality["Loading Product Data"])} />;
  }

  return (
    <>
      <Row>
        <Col xs={12} className="mb-4">
          <ProductBreadcrumbs
            categoryId={category}
            productId={productId}
            categoryDetails={categoryDetails}
            categoryNavData={categoryNavData}
            dashboardName={t(($) => $.product_quality["Content Dashboard"])}
            dashboardHref="/user/content/quality/org"
            getProductHref={(id) => `/user/content/seo?id=${id}`}
            hideDateSelector
          />
        </Col>
      </Row>
      <div className="main-content" style={{ backgroundColor: "#FFF" }}>
        <Grid fluid>
          <Row>
            <Col xs={12} className="pt-4">
              <Row>
                <Col xl={3} lg={4}>
                  <SideMenuTop
                    scores={scores}
                    images={images}
                    variations={variations}
                    saveDate={saveDate}
                    publishDate={publishDate}
                  />

                  <SideMenuBottom
                    getSearchTerms={getSearchTerms}
                    product={product}
                    seoKeywords={seoKeywords}
                    seoSearchTerms={seoSearchTerms}
                    allContentElements={allContentElements}
                    initialHiddenKeywords={hiddenKeywords}
                  />
                </Col>

                <Col xl={9} lg={8} className="px-4">
                  <div className="card fs-standard">
                    {editorMode === "edit" && (
                      <>
                        <Row>
                          <Col xs={12} className="px-5 pt-5 pb-5">
                            {product && (
                              <>
                                <Row>
                                  <Col className="text-start">
                                    <h4
                                      style={{
                                        marginTop: "5px",
                                        marginBottom: "0px",
                                      }}
                                    >
                                      {product.asin} / {product.sku}
                                    </h4>
                                  </Col>
                                  {userCanUpdateProducts && (
                                    <Col className="text-end" xs={4}>
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                        }}
                                      >
                                        <div className="pe-4">
                                          <Modal
                                            isOpen={showRecomendModal}
                                            onOpenChange={(isOpen) => setShowRecomendModal(isOpen)}
                                            title={t(($) => $.product_quality["Content Generation"])}
                                            css={{
                                              maxWidth: "60vw",
                                              padding: "xlarge",
                                            }}
                                          >
                                            <VStack justifyContent={"start"} width="full" gap={3}>
                                              <p className={css({ width: "full" })}>
                                                {t(
                                                  ($) =>
                                                    $.product_quality[
                                                      "Generate new content for this product using the top converting keywords, top performing products, and best practices."
                                                    ],
                                                )}
                                              </p>
                                              <TextAreaField
                                                className={css({ width: "full" })}
                                                value={generationNotes}
                                                onChange={(value) => setGenerationNotes(value)}
                                                placeholder={t(($) => $.product_quality["Context"])}
                                                label={t(($) => $.product_quality["Add additional context (optional)"])}
                                              />
                                              <HStack width="full" justifyContent="end">
                                                <Button
                                                  variant="secondary"
                                                  onPress={() => {
                                                    setShowRecomendModal(false);
                                                  }}
                                                >
                                                  {t(($) => $.product_quality["Cancel"])}
                                                </Button>
                                                <Button
                                                  onPress={() => {
                                                    setShowRecomendModal(false);
                                                    generateContent.mutate(generationNotes);
                                                  }}
                                                >
                                                  {t(($) => $.product_quality["Generate"])}
                                                </Button>
                                              </HStack>
                                            </VStack>
                                          </Modal>
                                          <Button
                                            onPress={() => {
                                              setShowRecomendModal(true);
                                            }}
                                            isDisabled={loadingGeneratedContent}
                                          >
                                            {t(($) => $.product_quality["Generate Content"])}
                                          </Button>
                                        </div>
                                        <div className="pe-4">
                                          {saveDate === null ?
                                            <Button
                                              onPress={() => {
                                                setContentChanged(false);
                                                getCatalog();
                                              }}
                                              isDisabled={!contentChanged}
                                            >
                                              {t(($) => $.product_quality["Reset"])}
                                            </Button>
                                          : <ConfirmationModal
                                              title={t(($) => $.product_quality["Reset Content"])}
                                              confirmationMessage={t(
                                                ($) =>
                                                  $.product_quality[
                                                    "You are about to reset the content for this product. This will remove all changes made to the content since the last save. Are you sure you want to continue?"
                                                  ],
                                              )}
                                              onConfirm={() => {
                                                resetProduct();
                                              }}
                                              confirmButtonText={t(($) => $.product_quality["Reset"])}
                                              modalButtonText={t(($) => $.product_quality["Reset"])}
                                              disabled={saveDate === null}
                                              modelButtonStyle={{ margin: 0 }}
                                            />
                                          }
                                        </div>
                                        <div className="pe-4">
                                          <Button
                                            isDisabled={!contentChanged}
                                            onPress={() => {
                                              saveProduct();
                                            }}
                                          >
                                            {t(($) => $.product_quality["Save"])}
                                          </Button>
                                        </div>
                                        <div>
                                          <ConfirmationModal
                                            title={t(($) => $.product_quality["Publish Content"])}
                                            confirmationMessage={
                                              <>
                                                <div>
                                                  {t(
                                                    ($) =>
                                                      $.product_quality[
                                                        "You are about to publish the content for this product. This will make the content live on the listing page of Amazon."
                                                      ],
                                                  )}
                                                </div>
                                                <div className="pt-3">
                                                  <strong>
                                                    {t(
                                                      ($) =>
                                                        $.product_quality[
                                                          "NOTE: It may take up to 24 hours for the changes to reflect on your Amazon Listing page"
                                                        ],
                                                    )}
                                                  </strong>
                                                </div>
                                                <div className="pt-3">
                                                  {t(($) => $.product_quality["Are you sure you want to continue?"])}
                                                </div>
                                              </>
                                            }
                                            onConfirm={() => {
                                              publishProduct();
                                            }}
                                            confirmButtonText={t(($) => $.product_quality["Publish"])}
                                            modalButtonText={t(($) => $.product_quality["Publish"])}
                                            disabled={!saveDate}
                                            modelButtonStyle={{ margin: 0 }}
                                          />
                                        </div>
                                      </div>
                                    </Col>
                                  )}
                                </Row>
                                <Row>
                                  <Col xs={12} className="mt-3 mb-3">
                                    <SteppedProgressBar
                                      currentStep={step}
                                      steps={[
                                        t(($) => $.product_quality["Analyzing keywords"]),
                                        t(($) => $.product_quality["Finding similar products"]),
                                        t(($) => $.product_quality["Generating content"]),
                                        t(($) => $.product_quality["Finishing up"]),
                                      ]}
                                      isLoading={loadingGeneratedContent}
                                    />
                                    {error && (
                                      <Alert
                                        dismissible
                                        onClose={() => setError(null)}
                                        variant="danger"
                                        style={{
                                          backgroundColor: "#f8d7da",
                                          borderColor: "#f5c6cb",
                                          color: "#152748",
                                        }}
                                      >
                                        <h4 style={{ marginTop: 0 }}>
                                          <i className="fa fa-exclamation-triangle" style={{ marginRight: "15px" }}></i>
                                          {t(($) => $.product_quality["Error publishing content"])}
                                        </h4>
                                        {error?.status === "INVALID" ?
                                          <ul>
                                            {error.issues.map((issue) => (
                                              <li key={issue.code}>{issue.message}</li>
                                            ))}
                                          </ul>
                                        : <p>
                                            {t(
                                              ($) =>
                                                $.product_quality[
                                                  "An error occurred. Please try again and contact support if the error persists"
                                                ],
                                            )}
                                          </p>
                                        }
                                      </Alert>
                                    )}
                                  </Col>
                                </Row>
                              </>
                            )}
                            <Row>
                              <Col className="border-bottom">
                                <Nav variant="underline" defaultActiveKey="content" {...navProps}>
                                  <Nav.Item>
                                    <Nav.Link eventKey="content">{t(($) => $.product_quality["Content"])}</Nav.Link>
                                  </Nav.Item>
                                  <Nav.Item>
                                    <Nav.Link eventKey="history">{t(($) => $.product_quality["History"])}</Nav.Link>
                                  </Nav.Item>
                                </Nav>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                        {currentNav === "history" && (
                          <Row>
                            <Col xs={12} className="px-5 pb-5">
                              <PublishHistory
                                productQualityId={data?.data?.id}
                                onApply={(revision) => {
                                  setTitle(revision.title);
                                  setDescription(revision.description);
                                  setFeatureBullets(revision.feature_bullets);
                                  setGenericKeywords(revision.generic_keywords);
                                  setHighlight(revision.highlight ?? "");
                                  setContentChanged(true);
                                }}
                              />
                            </Col>
                          </Row>
                        )}
                        {currentNav === "content" && (
                          <Row>
                            <Col xs={12} className="px-5 pb-5">
                              <Row className="pt-4">
                                <Col xs={7}>
                                  <h4 style={{ marginTop: 0 }}>
                                    {t(($) => $.product_quality["Product Title"])}&nbsp;
                                    <button
                                      className="fs-3"
                                      style={{
                                        border: 0,
                                        backgroundColor: "#fff",
                                      }}
                                      onClick={() => setShowTitleHelp(!showTitleHelp)}
                                    >
                                      <i className="fa fa-info-circle"></i>
                                    </button>
                                  </h4>
                                </Col>
                                <Col className="pe-4" xs={5}>
                                  <ConversionLegend />
                                </Col>
                              </Row>

                              {showTitleHelp && (
                                <div
                                  style={{
                                    border: "1px solid #9d7eb8",
                                    backgroundColor: "#e0b7d7",
                                    padding: "10px",
                                    marginBottom: "5px",
                                    borderRadius: "5px",
                                  }}
                                  className="fs-standard"
                                >
                                  <ul>
                                    <li>
                                      {t(($) => $.product_quality["Title should not contain any special characters"])}
                                    </li>
                                    <li>{t(($) => $.product_quality["Avoid using any emojis"])}</li>
                                    <li>
                                      {t(
                                        ($) =>
                                          $.product_quality[
                                            "Aim for 75 characters or less. Amazon requires titles of 75 characters or less starting July 27, 2026."
                                          ],
                                      )}
                                    </li>
                                    <li>
                                      {t(
                                        ($) =>
                                          $.product_quality[
                                            "It's best to have as many converting words or phrases as possible"
                                          ],
                                      )}
                                    </li>
                                    <li>
                                      {t(
                                        ($) =>
                                          $.product_quality[
                                            "Do not promote your items in your title (i.e. Free Shipping, For a limited time only)"
                                          ],
                                      )}
                                    </li>
                                  </ul>
                                </div>
                              )}
                              <ContentEditor2
                                field={"title"}
                                content={title}
                                minLength={0}
                                maxLength={TITLE_MAX_LENGTH}
                                recommendedMaxLength={TITLE_RECOMMENDED_LENGTH}
                                onContentChange={onContentChange}
                                setContent={setContent}
                                highlightPhrases={[...seoKeywords, ...seoSearchTerms]}
                                recommendedContent={recommendations?.title}
                                readOnly={!userCanUpdateProducts}
                              />
                              <Row className="pt-4">
                                <Col>
                                  <h4 style={{ marginTop: 0 }}>
                                    {t(($) => $.product_quality["Highlight"])}&nbsp;
                                    <button
                                      className="fs-3"
                                      style={{
                                        border: 0,
                                        backgroundColor: "#fff",
                                      }}
                                      onClick={() => setShowHighlightHelp(!showHighlightHelp)}
                                    >
                                      <i className="fa fa-info-circle"></i>
                                    </button>
                                  </h4>
                                </Col>
                              </Row>
                              {showHighlightHelp && (
                                <div
                                  style={{
                                    border: "1px solid #9d7eb8",
                                    backgroundColor: "#e0b7d7",
                                    padding: "10px",
                                    marginBottom: "5px",
                                    borderRadius: "5px",
                                  }}
                                  className="fs-standard"
                                >
                                  <ul>
                                    <li>
                                      {t(
                                        ($) =>
                                          $.product_quality[
                                            "A short highlight of materials or recommended uses. Shown with your title in search results and on the product page."
                                          ],
                                      )}
                                    </li>
                                  </ul>
                                </div>
                              )}
                              <ContentEditor2
                                content={highlight}
                                onContentChange={onContentChange}
                                setContent={setContent}
                                highlightPhrases={[...seoKeywords, ...seoSearchTerms]}
                                minLength={0}
                                maxLength={HIGHLIGHT_RECOMMENDED_LENGTH}
                                field={"highlight"}
                                recommendedContent={recommendations?.highlight}
                                readOnly={!userCanUpdateProducts}
                              />
                              <Row className="pt-4">
                                <Col>
                                  <h4 style={{ marginTop: 0 }}>
                                    {t(($) => $.product_quality["Bullet Points"])}&nbsp;
                                    <button
                                      className="fs-3"
                                      style={{
                                        border: 0,
                                        backgroundColor: "#fff",
                                      }}
                                      onClick={() => setShowBulletHelp(!showBulletHelp)}
                                    >
                                      <i className="fa fa-info-circle"></i>
                                    </button>
                                  </h4>
                                </Col>
                                <Col className="pe-4"></Col>
                              </Row>
                              {showBulletHelp && (
                                <div
                                  style={{
                                    border: "1px solid #9d7eb8",
                                    backgroundColor: "#e0b7d7",
                                    padding: "10px",
                                    marginBottom: "5px",
                                    borderRadius: "5px",
                                  }}
                                  className="fs-standard"
                                >
                                  <ul>
                                    <li>{t(($) => $.product_quality["Avoid using ALL CAPS"])}</li>
                                    <li>
                                      {t(($) => $.product_quality["Start each bullet point with a capital letter"])}
                                    </li>
                                    <li>
                                      {t(
                                        ($) =>
                                          $.product_quality[
                                            "Use 5 bullet points to maximize number of keywords included in your list."
                                          ],
                                      )}
                                    </li>
                                    <li>
                                      {t(
                                        ($) =>
                                          $.product_quality[
                                            "Using 150+ characters in each your bullets will help you to maximize the number of keywords included in your list."
                                          ],
                                      )}
                                    </li>
                                  </ul>
                                </div>
                              )}
                              <BulletPointEditor
                                bulletPoints={featureBullets}
                                minLength={150}
                                maxLength={250}
                                onContentChange={onContentChange}
                                setContent={setContent}
                                highlightPhrases={[...seoKeywords, ...seoSearchTerms]}
                                onRemoveBullet={onRemoveBullet}
                                recommendedContent={recommendations?.bullet_points}
                                isReadOnly={!userCanUpdateProducts}
                              />
                              <Row className="pt-4">
                                <Col>
                                  <h4 style={{ marginTop: 0 }}>{t(($) => $.product_quality["Keywords"])}</h4>
                                </Col>
                              </Row>
                              <ContentEditor2
                                content={genericKeywords}
                                onContentChange={onContentChange}
                                setContent={setContent}
                                highlightPhrases={[...seoKeywords, ...seoSearchTerms]}
                                minLength={140}
                                maxLength={1000}
                                field={"genericKeywords"}
                                readOnly={!userCanUpdateProducts}
                              />
                              <Row className="pt-4 mt-5">
                                <Col>
                                  <h4 style={{ marginTop: 0 }}>{t(($) => $.product_quality["Description"])}</h4>
                                </Col>
                              </Row>
                              <ContentEditor2
                                richText
                                content={description}
                                highlightPhrases={[...seoKeywords, ...seoSearchTerms]}
                                minLength={140}
                                maxLength={1000}
                                onContentChange={onContentChange}
                                field={"description"}
                                setContent={setContent}
                                readOnly={!userCanUpdateProducts}
                              />
                            </Col>
                          </Row>
                        )}
                      </>
                    )}

                    {editorMode === "metrics" && (
                      <>
                        <Row>
                          <Col xs={12} className="px-5 pt-3 text-end">
                            <button
                              type="button"
                              className="btn-close"
                              onClick={() => {
                                setEditorMode("edit");
                                // setQuickSearch(null);
                              }}
                              aria-label={t(($) => $.product_quality["Close"])}
                              style={{ fontSize: "20px" }}
                            ></button>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={12} className="px-5 pt-5 pb-5">
                            <h4 style={{ marginTop: 0 }}>
                              {t(($) => $.product_quality["Keyword Performance"])}{" "}
                              {keyword && <Badge bg={getBadgeVariant(keyword)}>{keyword["keyword_text"]}</Badge>}
                            </h4>
                            {!keyword && (
                              <Row className="fs-standard">
                                <Col className="text-center">
                                  {t(($) => $.product_quality["Select a keyword to view performance"])}
                                </Col>
                              </Row>
                            )}
                            {keyword && (
                              <Row className="fs-standard">
                                <Col xs={12}>
                                  <Table striped bordered hover>
                                    <thead>
                                      <tr>
                                        <th className="text-end">
                                          <strong>{t(($) => $.product_quality["Impressions"])}</strong>
                                        </th>
                                        <th className="text-end">
                                          <strong>{t(($) => $.product_quality["Clicks"])}</strong>
                                        </th>
                                        <th className="text-end">
                                          <strong>{t(($) => $.product_quality["CTR"])}</strong>
                                        </th>
                                        <th className="text-end">
                                          <strong>{t(($) => $.product_quality["Conversions"])}</strong>
                                        </th>
                                        <th className="text-end">
                                          <strong>{t(($) => $.product_quality["CR"])}</strong>
                                        </th>
                                        <th className="text-end">
                                          <strong>{t(($) => $.product_quality["Sales"])}</strong>
                                        </th>
                                        <th className="text-end">
                                          <strong>{t(($) => $.product_quality["ACoS"])}</strong>
                                        </th>
                                        <th className="text-end">
                                          <strong>{t(($) => $.product_quality["Cost"])}</strong>
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td className="text-end">{keyword.impressions}</td>
                                        <td className="text-end">{keyword.clicks}</td>
                                        <td className="text-end">{(keyword.ctr * 100).toFixed(2)}%</td>
                                        <td className="text-end">{keyword.conversions}</td>
                                        <td className="text-end">{(keyword.cr * 100).toFixed(2)}%</td>
                                        <td className="text-end">
                                          {formatCurrency(keyword.sales, {
                                            marketPlace: marketPlace.marketPlace,
                                          })}
                                        </td>
                                        <td className="text-end">{(keyword.acos * 100).toFixed(2)}%</td>
                                        <td className="text-end">
                                          {formatCurrency(keyword.cost, {
                                            marketPlace: marketPlace.marketPlace,
                                          })}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={12} className="px-5 pt-2 pb-5">
                            <h4 style={{ marginTop: 0 }}>{t(($) => $.product_quality["Associated Search Terms"])}</h4>
                            {!searchTerms && !loadingSearchTerms && (
                              <Row className="fs-standard">
                                <Col className="text-center">
                                  {t(($) => $.product_quality["Select a keyword to view the search terms"])}
                                </Col>
                              </Row>
                            )}
                            {loadingSearchTerms && (
                              <Row className="fs-standard">
                                <Col className="text-center">
                                  {t(($) => $.product_quality["Finding search terms..."])}
                                </Col>
                              </Row>
                            )}

                            {!loadingSearchTerms && searchTerms && (
                              <>
                                <p>
                                  {t(($) => $.product_quality["Below are the search terms that contain the keyword "])}
                                  <Badge bg={getBadgeVariant(keyword)}>{keyword["keyword_text"]}</Badge>
                                  {t(
                                    ($) =>
                                      $.product_quality[
                                        ", which have generated sales on the advertisements that contain this product ("
                                      ],
                                  )}
                                  <strong>
                                    {product.sku} / {product.asin}
                                  </strong>
                                  {")."}
                                </p>
                                <div
                                  style={{
                                    maxHeight: "500px",
                                    minHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table striped hover bordered>
                                    <thead>
                                      <tr>
                                        <th style={{ textAlign: "left" }}>
                                          <strong>{t(($) => $.product_quality["Search Term"])}</strong>
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                          <strong>{t(($) => $.product_quality["Impressions"])}</strong>
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                          <strong>{t(($) => $.product_quality["Clicks"])}</strong>
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                          <strong>{t(($) => $.product_quality["CTR"])}</strong>
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                          <strong>{t(($) => $.product_quality["CONVERSIONS"])}</strong>
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                          <strong>{t(($) => $.product_quality["CR"])}</strong>
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                          <strong>{t(($) => $.product_quality["Sales"])}</strong>
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                          <strong>{t(($) => $.product_quality["Cost"])}</strong>
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                          <strong>{t(($) => $.product_quality["ACOS"])}</strong>
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {searchTerms.map((term, index) => {
                                        return (
                                          <tr key={index}>
                                            <td>
                                              <DropdownButton title={term.keyword_text} bsPrefix="dropdown-menu-seo">
                                                <Dropdown.Item href="#/action-2">
                                                  {t(($) => $.product_quality["Add keyword to Media Plan"])}
                                                </Dropdown.Item>
                                              </DropdownButton>
                                            </td>
                                            <td className="text-end">{formatNumber(term.impressions)}</td>
                                            <td className="text-end">{formatNumber(term.clicks)}</td>
                                            <td className="text-end">{formatPercent(term.ctr)}%</td>
                                            <td className="text-end">{formatNumber(term.conversions)}</td>
                                            <td className="text-end">{formatPercent(term.cr)}%</td>
                                            <td className="text-end">
                                              {formatCurrency(term.sales, {
                                                marketPlace: marketPlace.marketPlace,
                                              })}
                                            </td>
                                            <td className="text-end">
                                              {formatCurrency(term.cost, {
                                                marketPlace: marketPlace.marketPlace,
                                              })}
                                            </td>
                                            <td className="text-end">{formatPercent(term.acos)}%</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </Table>
                                </div>
                              </>
                            )}
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Grid>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  marketPlace: state.marketPlace,
  modules: selectModules(state),
});

const ConnectedProductSEO = connect(mapStateToProps)(ProductSEO);

const ProductSEOPage = () => (
  <DatesProvider>
    <ConnectedProductSEO />
  </DatesProvider>
);

export const Route = createFileRoute("/_authenticated/user/content/seo")({
  component: ProductSEOPage,
  staticData: { application: "Product" },
});
